import { z } from 'zod';

/**
 * Status of a bilateral Connection between two parties.
 *
 * - `pending`  — one side requested, other has not responded
 * - `accepted` — both sides accept (or implied by a commercial action;
 *                see ADR #11 in `docs/relations-v2/01-decisions.md` for
 *                the auto-acceptance rule for commercial actions)
 * - `blocked`  — one side blocked; further commercial actions fail at the
 *                validation layer
 */
export const connectionStatusSchema = z.enum(['pending', 'accepted', 'blocked']);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/**
 * Optional relationship tags either party can attach to a Connection.
 * Used by "My Network" UI for filtering / annotation; carries no
 * authorization weight (does NOT gate commercial actions).
 *
 * - `preferred_carrier`   — partner prefers this carrier for new loads
 * - `preferred_customer`  — partner prefers this customer's loads
 * - `preferred_broker`    — partner prefers this broker for subcontracts
 * - `do_not_dispatch`     — do not auto-broadcast offers to this party
 * - `priority`            — surface this party first in counter-party pickers
 * - `on_hold`             — temporarily paused; not a permanent block
 * - `archived`            — hide from default views
 */
export const connectionTagSchema = z.enum([
  'preferred_carrier',
  'preferred_customer',
  'preferred_broker',
  'do_not_dispatch',
  'priority',
  'on_hold',
  'archived',
]);
export type ConnectionTag = z.infer<typeof connectionTagSchema>;
